import React, {forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState} from "react";
import ReactCreditCards from "../../../../custom/react-credit-cards";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import {CheckboxForm} from "../../../base/checkbox/checkbox";
import Form from "../../../base/form";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import {MuiRFF, Yup} from '../../../form';
import DateUtils from "../../../../../core/services/date-utils";
import Grid2 from "@mui/material/Unstable_Grid2";
import {CreditCardProviders} from "../../../../../core/constants/enums";
import useAccessLevels from "../../../../hooks/use-access-levels";


const schema = Yup.object().shape({
    number: Yup.string().trim()
        .test('cardNumberLen', 'Card number must be between 14 to 19 characters ',
            (val) => {
                val = val?.trim();
                if (!val?.trim()?.length)
                    return false;
                if (!Number.isInteger(Number(val)))
                    return false;
                return val?.length >= 14 && val?.length <= 19;
            }
        )
        .nullable().required(ValidateMessages.required),
    name: Yup.string().trim().nullable().required(ValidateMessages.required),
    cvc: Yup.string().trim()
        .test('cardNumberLen', 'CVV must be between 3 to 4 characters ',
            (val) => {
                val = val?.trim();
                if (!val?.trim()?.length)
                    return false;
                if (!Number.isInteger(Number(val)))
                    return false;
                return val?.length >= 3 && val?.length <= 4;
            }
        )
        .nullable().required(ValidateMessages.required),
    expiry: Yup.mixed()
        .nullable()
        .validDate(ValidateMessages.incorrect)
        .disablePastDate(ValidateMessages.minDate('this month'), 'month')
        .required(ValidateMessages.required),
    save: Yup.boolean().default(true).nullable(),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const NewMonerisCreditCardForm = forwardRef(({
                                                 onSuccess,
                                                 customerId,
                                                 setSubmitting,
                                                 id,
                                             }, ref) => {
    const [focused, setFocused] = useState(undefined);
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManagePaymentMethods = packages.sales.manageCustomerPaymentMethods;

    const initialValues = useMemo(() => ({
        name: '',
        expiry: undefined,
        cvc: '',
        number: '',
        save: true,
    }), [])


    /**
     * Attaches a [submit] callback to the passed reference to this component so that the parent component can
     * submit this form using their reference.
     */
    useImperativeHandle(ref, () => ({
        submit: (save = true) => {
            formRef.current?.change('save', save);
            formRef.current?.submit();
        },
    }), [formRef])

    /**
     * Creates a new moneris credit card.
     * @type {(creditCardInfo: any) => Promise<ApiResponseDs<any> | undefined>}
     */
    const addMonerisCreditCard = useCallback(async (creditCardInfo) => {
        if (!canManagePaymentMethods) return;
        const forApi = {
            holderName: creditCardInfo.HolderName,
            cardNumber: creditCardInfo.CardNumber,
            cvv: creditCardInfo.CVV,
            year: creditCardInfo.Year,
            month: creditCardInfo.Month,
            isDefault: false,
            customerId: customerId,
            paymentProvider: CreditCardProviders.moneris,
        }
        const response = await Api.addNewCustomerCreditCardV2(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            return {
                ...(response?.data ?? {}),
                providerName: CreditCardProviders.moneris,
            };
        }
    }, [canManagePaymentMethods, customerId, isMounted]);

    /**
     * submits the form for adding a credit card.
     *
     * - if [values.save] value is set to true, first creates a credit card in the system with the provided info, and
     * then calls the onSuccess callback.
     * - else, calls the onSuccess callback with the credit card information.
     *
     * @type {(values: any) => Promise<void>}
     */
    const onFormSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const creditCardInfo = {
            CardNumber: values.number,
            CVV: values.cvc,
            Year: Number(DateUtils.UTCToServer(values.expiry).format('YY')),
            Month: Number(DateUtils.UTCToServer(values.expiry).format('MM')),
            HolderName: values.name,
        };
        if (values?.save) {
            const createdCreditCard = await addMonerisCreditCard(creditCardInfo);
            if (!!createdCreditCard) {
                await onSuccess(createdCreditCard, true, true);
            }
        } else {
            await onSuccess(creditCardInfo, false);
        }
        setSubmitting(false);
    }, [addMonerisCreditCard, onSuccess, setSubmitting]);

    return (
        <>
            <Form
                id={id}
                onSubmit={onFormSubmit}
                initialValues={initialValues}
                validate={validate}
                render={({values, form}) => {
                    formRef.current = form;
                    return (
                        <Grid2 container spacing={3} sx={{position: 'relative'}}>
                            <Grid2 xs={12} sx={{position: 'absolute', zIndex: -1}}>
                                {/*Used only to determine if the credit card must be added as well*/}
                                <CheckboxForm
                                    className={'d-none'}
                                    name={`save`}
                                    required={false}
                                    data={[{value: values.save ?? true}]}
                                />
                            </Grid2>
                            <Grid2 sm={12} md={6}>
                                <ReactCreditCards
                                    cvc={values?.cvc}
                                    expiry={values?.expiry && values.expiry?.isValid()
                                        ? DateUtils.UTCToLocal(values.expiry).format('MM/YY')
                                        : ''
                                    }
                                    focused={focused}
                                    name={values?.name}
                                    number={values?.number}
                                />
                            </Grid2>
                            <Grid2 sm={12} md={6}>
                                <Grid2 container columns={12} spacing={2}>
                                    <Grid2 xs={12}>
                                        <OutlinedInputForm
                                            value={values.number ?? ''}
                                            label={'Card Number'}
                                            name={'number'}
                                            type={'number'}

                                            required={required.number}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            onFocus={(e) => setFocused(e.target.name)}
                                        />
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <OutlinedInputForm
                                            value={values.name ?? ''}
                                            label={'Card Holder Name'}
                                            name={'name'}
                                            required={required.name}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            onFocus={(e) => setFocused(e.target.name)}
                                        />
                                    </Grid2>
                                    <Grid2 xs={6}>
                                        <MuiRFF.DatePicker
                                            name={"expiry"}
                                            label={"Card Expiry"}
                                            views={['month', 'year']}
                                            required={required.expiry}
                                            format={'MM/YY'}
                                            disablePast
                                            textFieldProps={{
                                                onFocus: (e) => setFocused(e.target.name),
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 xs={6}>
                                        <OutlinedInputForm
                                            value={values.cvc ?? ''}
                                            label={'Card CVV'}
                                            name={'cvc'}
                                            type={'text'}
                                            required={required.cvc}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            onFocus={(e) => setFocused(e.target.name)}
                                            maxLength={4}
                                            inputProps={{
                                                maxLength: 4,
                                                minLength: 3,
                                            }}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    );
                }}/>
        </>
    )
})

export default NewMonerisCreditCardForm;
